// ═══════════════════════════════════════════════════════════════════════════════
// src/Layout.jsx
// Shell chrome: sidebar + user strip + system banner + <Outlet/> for routed views.
// Extracted from the old App.jsx so routing lives at the top and chrome lives
// here. State-based activeNav is gone; the active item is derived from URL.
// ═══════════════════════════════════════════════════════════════════════════════

import { lazyRetry } from "./lib/chunkReload";
import { useState, useEffect, useRef, useCallback, useMemo, Suspense } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { PageSkeleton } from "./components/skeletons";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { getThemePreference, setThemePreference, DARK_MODE_READY, DARK_MODE_PREVIEW, isPreviewOptedIn, setPreviewOptIn } from "./lib/theme";
import { AssistantDrawerProvider } from "./components/assistant/AssistantDrawerContext";
import AssistantDrawer from "./components/assistant/AssistantDrawer";
import AccountMenu from "./components/AccountMenu";
import { Btn } from "./components/ui";
import { hiddenToday as eodHiddenToday } from "./components/BeforeYouGo";
import { useInboxCounts, useShellCount, useShellFeed } from "./hooks/useShellCounts";
import { fetchEodSweep, readInsuranceUpdatesCount, eodOpenCount, eodNudgeText, zonedClock } from "./lib/shellCounts";
import { INBOX_DOCS_ONLY_ROLES } from "./lib/tokens";
import { useAuth } from "./auth/AuthProvider";
import { supabase } from "./lib/supabaseClient";
import { useFeatureAccess } from "./hooks/useFeatureAccess";
import { C, NAV_BY_ROLE, NAV_META, NAV_SECTIONS, ROLE_STYLES } from "./lib/tokens";
// The patient portal is its own world (~220 KB). Staff never render it, so it
// loads only when a Patient signs in, instead of in every staff sign-in.
const PortalShell = lazyRetry(() => import("./views/PortalShell"));
import CommandPalette       from "./components/CommandPalette";
import ShortcutsOverlay     from "./components/ShortcutsOverlay";
import DarkPreviewBanner    from "./components/DarkPreviewBanner";
import RolePreviewSheet     from "./components/RolePreviewSheet";
import NotificationsBell    from "./components/NotificationsBell";
import {
  IconSearch, IconGrid, IconCalendar, IconUsers, IconLayers, IconCheckSquare,
  IconClipboard, IconFileText, IconInbox, IconIdBadge, IconCreditCard, IconClock,
  IconChart, IconShield, IconShieldCheck, IconEdit, IconHeart, IconTrendingUp,
  IconReceipt, IconSettings, IconBell, IconAddressBook, IconBuilding, IconSparkles,
  IconSend, IconMessage,
} from "./components/icons";
import ProSystemAlertBanner from "./components/pro/ProSystemAlertBanner";
import SpectatorBanner      from "./auth/SpectatorBanner";
import { screenKey, getTextScale, applyTextScale, stepTextScale, setTextScale } from "./lib/textScale";
import IdleSignOut from "./lib/idleSignOut";
import { useInboundSmsBadge } from "./hooks/useInboundSmsBadge";
import { DriverLocationReporter } from "./components/rides/DriverAlerts";
import { setRideTimeZone } from "./lib/ridesCore";
import { derivePageTitle, documentTitleFor } from "./lib/pageTitleCore";
import { usePhoneTitleOverride } from "./lib/pageTitle";
import { routeKey } from "./lib/routeKey";

// Sidebar nav id -> URL path. Colocated with the sidebar rendering (not in
// tokens.js) so URL changes don't require a design-token edit.
const NAV_PATHS = {
  dashboard:         "/dashboard",
  schedule:          "/schedule",
  care_schedule:     "/care-schedule",
  patients:          "/patients",
  queue:             "/queue",
  tasks:             "/tasks",
  clinical:          "/clinical",
  inbox:             "/inbox",
  staff:             "/staff",
  reports:           "/reports",
  settings:          "/settings",
  billing:           "/billing",
  eligibility:       "/eligibility",
  waitlist:          "/waitlist",
  referrals:         "/referrals",
  insights:          "/insights",
  compliance:        "/compliance",
  consents:          "/consents",
  care_management:   "/care-management",
  cm_copilot:        "/care-management/copilot",
  insurance_updates: "/insurance-updates",
  recall:            "/recall",
  campaigns:         "/campaigns",
  portal:            "/portal",
  pro_chart_prep:    "/pro/chart-prep",
  pro_hrsn:          "/pro/hrsn",
  pro_assistant:     "/pro/assistant",
  pro_outreach:      "/pro/outreach",
  pro_inbound_sms:   "/pro/inbound-sms",
  chw_engagements:   "/chw-engagements",
  my_people:         "/my-people",
  my_settings:       "/my-settings",
  org_console:       "/org",
};

// Pro nav metadata (icon + label). Kept inline so tokens.js doesn't change.
const PRO_NAV_META_LOCAL = {
  pro_chart_prep:  { icon: "📋", label: "Chart Prep" },
  pro_hrsn:        { icon: "🤝", label: "HRSN" },
  pro_assistant:   { icon: "🤖", label: "AI Assistant" },
  pro_outreach:    { icon: "📤", label: "Outreach" },
  pro_inbound_sms: { icon: "💬", label: "Inbound SMS" },
};

// Labels for the nav ids rendered as one-off sidebar buttons (not in NAV_META):
// the CM Co-pilot item and the Network Console. Same text the sidebar shows.
const OFF_META_LABELS = { cm_copilot: "CM Co-pilot", org_console: "Network Console" };

// Page title for a nav id - the single source for the phone top bar and the
// browser tab title, so they always read the same as the sidebar.
const navLabelFor = (id) => (NAV_META[id] || {}).label || (PRO_NAV_META_LOCAL[id] || {}).label || OFF_META_LABELS[id];

// Sidebar line-icon set (replaces the emoji glyphs from NAV_META). Falls back
// to the emoji if an id is unmapped, so nothing renders blank.
const NAV_ICONS = {
  dashboard: IconGrid, schedule: IconCalendar, care_schedule: IconClipboard,
  patients: IconUsers, clinical: IconFileText, waitlist: IconClock, referrals: IconSend,
  queue: IconLayers, tasks: IconCheckSquare, inbox: IconInbox,
  staff: IconIdBadge, eligibility: IconShieldCheck, insurance_updates: IconCreditCard,
  billing: IconReceipt, recall: IconBell, campaigns: IconTrendingUp,
  reports: IconFileText, insights: IconChart, compliance: IconShield,
  consents: IconEdit, care_management: IconHeart, settings: IconSettings,
  chw_engagements: IconUsers, my_people: IconAddressBook, my_settings: IconSettings, org_console: IconBuilding, administrator: IconShieldCheck,
  pro_chart_prep: IconFileText, pro_hrsn: IconHeart, pro_assistant: IconSparkles,
  pro_outreach: IconSend, pro_inbound_sms: IconMessage, cm_copilot: IconSparkles,
};

// Active-nav accent color (brand-mid, retints per palette).
const NAV_ACCENT = "var(--brand-primary-mid, #1D9E75)";

// Inject a keyboard focus-visible ring for sidebar nav items once. Inline styles
// cannot express :focus-visible, so a tiny stylesheet handles it (SSR-guarded).
if (typeof document !== "undefined" && !document.getElementById("pos-nav-focus-style")) {
  const posNavFocusEl = document.createElement("style");
  posNavFocusEl.id = "pos-nav-focus-style";
  posNavFocusEl.textContent =
    ".pos-nav-item:focus-visible{outline:2px solid var(--brand-primary-mid,#1D9E75);outline-offset:1px;border-radius:8px;}" +
    // Kinetic icon, matching the chart home tiles so the two surfaces share
    // one motion language: the row stays put, the glyph springs. Same curve
    // and timing as .pos-chart-tile svg in ChartHome.jsx - if you change one,
    // change both, or the app feels like two products.
    ".pos-nav-item .pos-nav-glyph{transition:transform .22s cubic-bezier(.34,1.56,.64,1);}" +
    ".pos-nav-item:hover .pos-nav-glyph{transform:scale(1.18) rotate(-5deg);}" +
    "@media (prefers-reduced-motion: reduce){" +
      ".pos-nav-item .pos-nav-glyph{transition:none;}" +
      ".pos-nav-item:hover .pos-nav-glyph{transform:none;}" +
    "}";
  document.head.appendChild(posNavFocusEl);
}

// One glyph renderer used by every sidebar row. Active rows get the brand
// accent color; inactive rows inherit the row's (dimmed) color.
const NavGlyph = ({ id, active }) => {
  const Ic = NAV_ICONS[id];
  return (
    <span className="pos-nav-glyph" style={{ display: "inline-flex", color: active ? NAV_ACCENT : "inherit", flexShrink: 0 }}>
      {Ic ? <Ic size={17} /> : <span style={{ fontSize: 14 }}>{(NAV_META[id] || PRO_NAV_META_LOCAL[id] || {}).icon}</span>}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Sidebar alert treatment: "icon fill + odometer count" hybrid.
//
// Replaces the red pill badge. Two parts:
//
// 1. NavFillGlyph - the nav icon itself acts as a meter. The icon renders
//    twice, stacked: a dim base copy, and an amber copy clipped to a
//    bottom-aligned window whose height tracks how full the queue is
//    (count / capacity, capped at 100%). Empty queue = plain icon. As
//    items pile up the glyph "fills" from the bottom like a battery.
//
// 2. OdometerCount - a small muted tabular-nums count next to the label.
//    When the count changes (polling or realtime), the old digit slides
//    up and the new digit slides in from below, odometer style. The
//    motion is the alert: a rolling digit in peripheral vision means new
//    work just arrived. At rest it's quiet.
//
// The notification bell is deliberately excluded - it keeps its own
// treatment in NotificationsBell.jsx.
// ---------------------------------------------------------------------------

// Per-nav "full" thresholds for the icon fill. A queue at or above this
// count renders a fully filled glyph. Tuned for a small practice's normal
// day; revisit when pilot data shows real distributions.
const NAV_FILL_CAPACITY = { inbox: 15, insurance_updates: 10 };
// Amber - attention, not danger. Lives in tokens.js beside navBg because the
// nav surface is fixed in both themes; see the comment there.
const NAV_FILL_COLOR = C.navAttention;

const NavFillGlyph = ({ id, active, count }) => {
  const Ic = NAV_ICONS[id];
  if (!Ic) return <NavGlyph id={id} active={active} />;
  const capacity = NAV_FILL_CAPACITY[id] || 12;
  const pct = Math.min((count || 0) / capacity, 1);
  if (pct === 0) return <NavGlyph id={id} active={active} />;
  const fillHeightPx = Math.max(3, Math.round(17 * pct));
  return (
    <span style={{ position: "relative", display: "inline-flex", width: 17, height: 17, flexShrink: 0 }}>
      <span style={{ position: "absolute", inset: 0, display: "inline-flex", color: active ? NAV_ACCENT : "inherit", opacity: 0.55 }}>
        <Ic size={17} />
      </span>
      <span
        aria-hidden="true"
        style={{
          position: "absolute", left: 0, right: 0, bottom: 0,
          height: fillHeightPx, overflow: "hidden",
        }}
      >
        <span style={{ position: "absolute", left: 0, bottom: 0, display: "inline-flex", color: NAV_FILL_COLOR }}>
          <Ic size={17} />
        </span>
      </span>
    </span>
  );
};

// Rolling count. Rendering two layers during a transition: the outgoing
// number slides up and fades, the incoming slides in from below. Pure
// CSS transitions keyed on the count value - no animation library.
const OdometerCount = ({ count, testId }) => {
  const [display, setDisplay] = useState({ current: count, prev: null, rolling: false });
  useEffect(() => {
    setDisplay((d) => {
      if (d.current === count) return d;
      return { current: count, prev: d.current, rolling: true };
    });
    const t = setTimeout(() => {
      setDisplay((d) => ({ ...d, prev: null, rolling: false }));
    }, 320);
    return () => clearTimeout(t);
  }, [count]);
  if (!count || count <= 0) return null;
  return (
    <span
      aria-label={count + " open items"}
      data-testid={testId}
      style={{
        position: "relative", display: "inline-block",
        height: 16, minWidth: 16, overflow: "hidden",
        fontSize: 11, fontWeight: 600, lineHeight: "16px",
        fontVariantNumeric: "tabular-nums", textAlign: "right",
        color: NAV_FILL_COLOR,
      }}
    >
      {display.prev !== null && (
        <span style={{
          position: "absolute", right: 0, top: 0,
          transform: "translateY(-100%)", opacity: 0,
          transition: "transform 0.3s ease-out, opacity 0.3s ease-out",
        }}>{display.prev}</span>
      )}
      <span
        key={display.current}
        style={{
          position: "absolute", right: 0, top: 0,
          animation: display.rolling ? "posOdoIn 0.3s ease-out" : "none",
        }}
      >{display.current}</span>
    </span>
  );
};

// Keyframes for the odometer roll-in. Injected once alongside the
// focus-visible stylesheet pattern above.
if (typeof document !== "undefined" && !document.getElementById("pos-odometer-style")) {
  const posOdoEl = document.createElement("style");
  posOdoEl.id = "pos-odometer-style";
  posOdoEl.textContent = "@keyframes posOdoIn{from{transform:translateY(100%);opacity:0;}to{transform:translateY(0);opacity:1;}}";
  document.head.appendChild(posOdoEl);
}

// Shared row style. Active = translucent brand-tint pill with a 3px inset
// accent bar on the left and white text; inactive = borderless and dimmed.
const navItemStyle = (active, collapsed) => ({
  width: "100%", display: "flex", alignItems: "center", gap: 10,
  padding: collapsed ? "10px 0" : "9px 11px", marginBottom: 3,
  background: active ? "color-mix(in srgb, " + NAV_ACCENT + " 20%, transparent)" : "transparent",
  border: "none", borderRadius: 8,
  boxShadow: active ? "inset 3px 0 0 " + NAV_ACCENT + ", inset 0 1px 0 rgba(255,255,255,0.05)" : "none",
  // The sidebar is dark navy in BOTH themes, so its label must stay near-white.
  color: active ? C.onAccent : "rgba(255,255,255,0.58)",
  fontSize: 13, fontWeight: active ? 600 : 500, cursor: "pointer",
  textAlign: "left", justifyContent: collapsed ? "center" : "flex-start",
  transition: "background 0.12s, color 0.12s",
});

// Care Schedule is now declared in NAV_BY_ROLE and NAV_META directly (see
// tokens.js). The handcrafted runtime injection that used to live here moved
// to data; the "Today" section in NAV_SECTIONS handles placement.

// Roles an owner can preview. Patient is absent on purpose: that experience is
// the portal, a separate shell, so previewing it here would wrap patient
// screens in staff chrome and show something that does not exist.
const ROLE_PREVIEWS = [
  "Owner", "Manager", "Provider", "Medical Assistant", "Front Desk", "Billing",
  "Care Manager", "Supervising Care Manager", "CHW",
];

// Layout is a thin switch. The patient portal and the staff shell are separate
// components so each always calls the same hooks in the same order. Previously
// the Patient check returned early from the middle of the staff shell, after
// some hooks and before about ten others: any role change without a remount
// (a late-loading role, sign-out then sign-in) changed the hook count and React
// threw "Rendered more hooks than during the previous render", blanking the app.
export default function Layout() {
  const { role } = useAuth();
  if (role === "Patient") return <Suspense fallback={<PageSkeleton label="Loading your portal" />}><PortalShell /></Suspense>;
  return <StaffLayout />;
}

function StaffLayout() {
  const { profile, role, practiceId, tier, practiceName, isSuperAdmin, signOut,
          viewAsRole, setViewAsRole, realRole } = useAuth();
  const previewingRole = Boolean(viewAsRole && viewAsRole !== realRole);
  // Ride times show and are entered in the practice's time zone.
  const practiceTz = profile && profile.practices && profile.practices.timezone;
  useEffect(() => { setRideTimeZone(practiceTz || null); }, [practiceTz]);
  const [themePref, setThemePref] = useState(getThemePreference);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // With dark mode generally available the control is always "on"; the
  // opt-in state only matters while it is a preview.
  const [previewOn, setPreviewOn] = useState(() => DARK_MODE_READY || isPreviewOptedIn());
  const [roleSheetOpen, setRoleSheetOpen] = useState(false);
  const navigate = useNavigate();

  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // End-of-day nudge (round 4 #9): once per day, after 15:00 in the PRACTICE's
  // time zone, point staff at the "Before you go" card - but only when that
  // card has something to show for this user. It reads the same eod_sweep
  // entry as the card (lib/shellCounts.js), so on the Dashboard the two share
  // one request. It used to fire for everyone at 15:00 device time as a toast
  // that sat over Settings' Save button and the Inbox key hints for 8s; it is
  // now a slim in-flow line at the top of the page (EodNudge below), so it
  // pushes content down instead of covering it, and says how much is open.
  // "Hide for today" on the card also silences it. Not shown on /dashboard,
  // where the card itself is on screen (and not marked seen there either).
  const [eodNudge, setEodNudge] = useState(null); // { count } while showing
  useEffect(() => {
    if (!role || role === "Patient" || role === "System") return undefined;
    let alive = true;
    const check = async () => {
      const { hour, day } = zonedClock(new Date(), practiceTz);
      if (hour < 15 || pathRef.current === "/dashboard" || eodHiddenToday()) return;
      let seen = null;
      try { seen = localStorage.getItem("pos_eod_notified"); } catch (e) { /* per-device convenience only */ }
      if (seen === day) return;
      let sweep = null;
      try {
        // A result up to 5 minutes old (the card's own read) is reused.
        sweep = await fetchEodSweep({ maxAgeMs: 5 * 60 * 1000 });
      } catch (e) {
        // The nudge is optional: no sweep, no nudge. The card shows its own
        // "Couldn't load" state where it renders.
        console.warn("[Layout] end-of-day check failed:", e && e.message);
        return;
      }
      const n = eodOpenCount(sweep);
      if (!alive || n <= 0) return;
      try { localStorage.setItem("pos_eod_notified", day); } catch (e) { /* per-device convenience only */ }
      setEodNudge({ count: n });
    };
    const t0 = setTimeout(check, 1500);
    // Every 10 minutes until it has shown today (an open item can appear later).
    const t1 = setInterval(check, 10 * 60 * 1000);
    return () => { alive = false; clearTimeout(t0); clearInterval(t1); };
  }, [role, practiceTz]);
  // Leaving the page puts the nudge away (it already did its job, once a day).
  const shownOnPath = useRef(null);
  useEffect(() => {
    if (!eodNudge) { shownOnPath.current = null; return; }
    if (shownOnPath.current === null) shownOnPath.current = location.pathname;
    else if (shownOnPath.current !== location.pathname) setEodNudge(null);
  }, [eodNudge, location.pathname]);

  // Per-page text size (lib/textScale.js). Each page remembers its own size
  // on this device; the routed pane and dialogs are zoomed by the CSS var.
  const pageKey = screenKey(location.pathname);
  const [textScale, setTextScaleState] = useState(1);
  useEffect(() => {
    const v = getTextScale(pageKey);
    setTextScaleState(v);
    applyTextScale(v);
  }, [pageKey]);
  const changeTextScale = useCallback((dir) => {
    setTextScaleState(dir === 0 ? setTextScale(pageKey, 1) : stepTextScale(pageKey, dir));
  }, [pageKey]);
  // Ctrl/Cmd + Alt + (= / - / 0): larger, smaller, reset. e.code, because
  // Alt rewrites e.key on macOS.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || !e.altKey) return;
      const dir = e.code === "Equal" || e.code === "NumpadAdd" ? 1
        : e.code === "Minus" || e.code === "NumpadSubtract" ? -1
        : e.code === "Digit0" || e.code === "Numpad0" ? 0 : null;
      if (dir === null) return;
      e.preventDefault();
      changeTextScale(dir);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [changeTextScale]);
  const mainRef  = useRef(null);

  // Tier 2 UI gates: practice preferences hide nav items for any module
  // or feature the practice has turned off in the onboarding wizard, even
  // when the subscription tier supports it. Same boolean source as the
  // server-side Tier 1 EF gates, so the rail matches what the EFs will run.
  //
  // These now come from useFeatureAccess() - the SAME predicates App.jsx's
  // CapabilityRoute uses - so the rail and the router cannot disagree about
  // what this practice can reach.
  const { can, prefs, isProTier } = useFeatureAccess();

  const showCmCopilot = can.cmCopilot();

  const navItems = (NAV_BY_ROLE[role] || []).filter((id) => {
    // Care Management is Pro/Command only, and hidden when the practice does
    // not deliver CM. can.careManagement() folds both checks together.
    if (id === "care_management" && !can.careManagement()) return false;
    return true;
  })
    // Reports and IC Insights are one "Insights" area (Oct 2026): a role with
    // either keeps ONE entry, /insights. What it shows inside is still decided
    // per role by lib/insightsNav (NAV_BY_ROLE itself is unchanged).
    .map((id) => (id === "reports" ? "insights" : id))
    .filter((id, i, all) => all.indexOf(id) === i)
    .concat(
    // My Settings is personal, not a role tab: the patient-ready text setting
    // lives on the user's own provider record, so any staff user who has one
    // gets the tab (Providers included, who have no practice Settings).
    role && role !== "Patient" && profile?.provider_id ? ["my_settings"] : []
  );

  // CHWs keep HRSN - they run SDOH screenings and need the who-is-due list -
  // but not the rest of the Pro AI tooling. That carve-out now lives in the
  // capability predicates themselves.
  // Stable across renders while the role's nav is unchanged (NotificationsBell
  // keys its polling on this set; see the request-budget smoke journey).
  const navKey = navItems.join(",");
  const navIdSet = useMemo(() => new Set(navKey ? navKey.split(",") : []), [navKey]);

  const proNavIds = [
    can.chartPrep()  && "pro_chart_prep",
    can.hrsn()       && "pro_hrsn",
    can.aiAssistant()&& "pro_assistant",
    can.outreach()   && "pro_outreach",
    can.inboundSms() && "pro_inbound_sms",
  ].filter(Boolean);
  // Inbound SMS: texts waiting for a reply + open safety flags (realtime).
  const inboundSms = useInboundSmsBadge(proNavIds.includes("pro_inbound_sms"), practiceId);
  // Round 7 #2: every shell badge above and below (sidebar, bell, Inbound
  // SMS, system alert banner, driver status) comes from this one read a
  // minute (shell_counts), the only shell poll that runs in a hidden tab.
  useShellFeed({ navIds: navIdSet, inboundSms: proNavIds.includes("pro_inbound_sms"), tier });

  // Phones: sidebar defaults collapsed and the toggle moves to the top of the
  // rail (thumb-reachable on login); tapping a nav item re-collapses it.
  const [isMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches
  );
  const [collapsed, setCollapsed]       = useState(() => {
    // Mobile always starts collapsed; on desktop, remember the user's choice.
    const mob = typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches;
    if (mob) return true;
    try { return localStorage.getItem("pos_sidebar_collapsed") === "1"; } catch (e) { return false; }
  });

  // Remember the collapse choice across reloads on desktop. Skip on mobile so a
  // phone session (which always starts collapsed) never overwrites the pref.
  useEffect(() => {
    if (isMobile) return;
    try { localStorage.setItem("pos_sidebar_collapsed", collapsed ? "1" : "0"); } catch (e) { /* ignore */ }
  }, [collapsed, isMobile]);
  const [cmdkOpen, setCmdkOpen]         = useState(false);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || "");

  // Global Cmd/Ctrl+K toggles the command palette (quick patient switcher +
  // page jump). Staff shell only - the Patient role returns earlier.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCmdkOpen((o) => !o);
        return;
      }
      // "?" opens the shortcut sheet, but never while the user is typing.
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if (e.key === "?" && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setShortcutsOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Derive active sidebar item from the URL.
  // Sort by path length descending so the more-specific match wins
  // (e.g. "/pro/chart-prep" beats "/pro" if "/pro" ever becomes a nav id).
  const activeNav = (() => {
    const entries = Object.entries(NAV_PATHS).sort((a, b) => b[1].length - a[1].length);
    const match = entries.find(([, p]) =>
      location.pathname === p || location.pathname.startsWith(p + "/")
    );
    // No match highlights nothing. Falling back to the first nav item lit
    // Dashboard on pages that have no nav entry (Onboarding, Org), which told
    // the user they were somewhere they were not (round 6 bug 3).
    return match ? match[0] : null;
  })();

  // Where you are, in words. The phone top bar shows it (a view may refine it
  // with usePhoneTitle, e.g. a care manager's name); the browser tab shows the
  // route title only, never the override - see lib/pageTitle.js for why.
  const routeTitle = derivePageTitle(location.pathname, NAV_PATHS, navLabelFor);
  const phoneTitleOverride = usePhoneTitleOverride();
  const phoneTitle = phoneTitleOverride || routeTitle;
  useEffect(() => { document.title = documentTitleFor(routeTitle); }, [routeTitle]);
  // Leaving the staff shell (sign out) puts the plain product name back, so
  // the sign-in screen does not keep the last page's title.
  useEffect(() => () => { document.title = documentTitleFor(null); }, []);

  // Click handler used by the sidebar AND exposed to child views via outlet
  // context. Maintains backward compat with views that still call onNav('x').
  const onNav = (id) => {
    const path = NAV_PATHS[id];
    if (isMobile) setCollapsed(true);
    navigate(path || "/dashboard");
  };

  // The page this URL shows (lib/routeKey.js): every section of one patient's
  // chart, and every Care Management section, is one page. It keys the routed
  // pane below, so a section click re-renders the page in place instead of
  // remounting it (round 6 #1).
  const pageRouteKey = routeKey(location.pathname);

  // Scroll reset on a real page change. <main> owns scrolling (not window),
  // so reset the main element's scrollTop, not window.scrollTo. Keyed on the
  // page, not the pathname: a chart or CM section click keeps the page (and
  // its sticky header) where it is; a different patient starts at the top.
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pageRouteKey]);

  // Sidebar badges (round 4 #3). The Inbox badge is the open-item count the
  // Inbox page shows ("3 open"), read from the one shared cache entry the
  // page and the bell also use; it used to count unread portal messages, so
  // the badge and the page disagreed. Layout is the one live subscriber: 60s
  // poll plus a debounced realtime refresh on inbox_items, never once per
  // route change (that refetched both counts on every click). Insurance
  // updates is the same cache entry as the bell's row. A failed count keeps
  // the last good number, or shows none; never a made-up zero.
  const inboxBadgeOn = !!practiceId && navIdSet.has("inbox") && !INBOX_DOCS_ONLY_ROLES.includes(role);
  const inboxCounts = useInboxCounts({ enabled: inboxBadgeOn, live: true, practiceId });
  const insuranceCount = useShellCount("insurance_updates", () => readInsuranceUpdatesCount(practiceId),
    { enabled: !!practiceId && navIdSet.has("insurance_updates"), scope: [practiceId] });
  const badgeCounts = { inbox: inboxCounts.value || 0, insurance_updates: insuranceCount.value || 0 };
  const badgeError = { inbox: !!inboxCounts.error, insurance_updates: !!insuranceCount.error };

  // Org membership check for the Network nav section. RLS on organizations
  // returns rows only for active org members, so a non-empty result is the
  // gate. Cosmetic only; enforcement is RLS + the membership-gated RPC.
  const [hasOrg, setHasOrg] = useState(false);
  useEffect(() => {
    let cancelled = false;
    supabase
      .from("organizations")
      .select("id")
      .limit(1)
      .then(({ data, error }) => {
        if (error) {
          // Fail closed: keep the Network section hidden on error.
          console.warn("[Layout] org membership check failed:", error.message);
          if (!cancelled) setHasOrg(false);
          return;
        }
        if (!cancelled) setHasOrg((data || []).length > 0);
      });
    return () => { cancelled = true; };
  }, []);

  const roleStyle = ROLE_STYLES[role] || {};

  // Palette "Pages" group: the role's accessible nav destinations only.
  const cmdkPages = navItems
    .map((id) => ({ id, label: (NAV_META[id] || {}).label || id, path: NAV_PATHS[id], icon: (NAV_META[id] || {}).icon }))
    .filter((p) => p.path);

  // Palette "Actions" group (UI Excellence item 9). Every entry navigates to a
  // route that exists - the first pass shipped six actions of which four
  // pointed at query params nothing reads (?new=1, ?log=1, ?tab=, ?show=),
  // so they silently landed on the page and did nothing. Destinations here are
  // taken from App.jsx, and each is gated by the same nav visibility the
  // sidebar uses, so the palette never offers something the role cannot open.
  const canSee = (id) => navItems.includes(id);
  const cmdkActions = [
    // Care management - the densest surface, and where the CM role lives all day.
    ...(canSee("care_management") ? [
      { id: "cm-today", label: "My day (care management)", keywords: "today worklist cm queue", icon: "\u2600", run: () => navigate("/care-management/today") },
      // These OPEN the dialog rather than landing near it: the destination view
      // reads ?open=<key> via useOpenParam and fires once on arrival.
      { id: "cm-log-touchpoint", label: "Log touchpoint", keywords: "new touchpoint contact call outreach record", icon: "\u260E", hint: "Opens the form", run: () => navigate("/care-management/touchpoints?open=log") },
      { id: "cm-batch-log", label: "Batch log touchpoints", keywords: "bulk multiple touchpoints", hint: "Opens the form", run: () => navigate("/care-management/touchpoints?open=batch") },
      { id: "cm-touchpoints", label: "Touchpoints", keywords: "touchpoint history list", icon: "\u260E", run: () => navigate("/care-management/touchpoints") },
      { id: "cm-new-enrollment", label: "New CM enrollment", keywords: "enroll member care management add", hint: "Opens the form", run: () => navigate("/care-management/registry?open=enroll") },
      { id: "cm-registry", label: "CM registry", keywords: "members panel caseload enrolled", run: () => navigate("/care-management/registry") },
      { id: "cm-assignments", label: "Attribution worklist", keywords: "assignments ba file attributed unmatched", run: () => navigate("/care-management/assignments") },
      { id: "cm-quality", label: "Quality dashboard", keywords: "measures gaps hedis amh projected supplemental", run: () => navigate("/care-management/quality") },
      { id: "cm-hedis", label: "HEDIS gaps", keywords: "care gaps measure close", run: () => navigate("/care-management/hedis") },
      { id: "cm-plans", label: "Care plans", keywords: "goals plan interventions", run: () => navigate("/care-management/plans") },
      { id: "cm-assessments", label: "Assessments", keywords: "comprehensive assessment reassessment", run: () => navigate("/care-management/assessments") },
      { id: "cm-transitions", label: "Transitions of care", keywords: "toc discharge adt hospital", run: () => navigate("/care-management/transitions") },
      { id: "cm-new-conference", label: "Schedule care conference", keywords: "new conference meeting team invite", hint: "Opens the form", run: () => navigate("/care-management/conferences?open=new") },
      { id: "cm-conferences", label: "Care conferences", keywords: "conference meeting team list", run: () => navigate("/care-management/conferences") },
      { id: "cm-billing", label: "CM billing readiness", keywords: "claims pmpm billable month", run: () => navigate("/care-management/billing") },
      { id: "cm-prl", label: "PRL exchange", keywords: "patient risk list outbound plan file", run: () => navigate("/care-management/prl") },
      { id: "cm-chw", label: "CHW engagements (care management)", keywords: "community health worker visits", run: () => navigate("/care-management/chw") },
      { id: "cm-vbp", label: "VBP contracts", keywords: "value based payment contract measures", run: () => navigate("/care-management/vbp") },
      { id: "cm-supervisor", label: "Supervisor dashboard", keywords: "oversight caseload team cm performance", run: () => navigate("/care-management/supervisor") },
    ] : []),
    // Daily operations.
    ...(canSee("schedule") ? [{ id: "go-schedule", label: "Schedule", keywords: "appointments calendar book", run: () => navigate("/schedule") }] : []),
    ...(canSee("queue") ? [{ id: "go-queue", label: "Live queue", keywords: "check in waiting roomed floor", run: () => navigate("/queue") }] : []),
    ...(canSee("inbox") ? [{ id: "go-inbox", label: "Inbox", keywords: "messages tasks labs refills claim", run: () => navigate("/inbox") }] : []),
    ...(canSee("tasks") ? [{ id: "go-tasks", label: "Tasks", keywords: "todo open work", run: () => navigate("/tasks") }] : []),
    ...(canSee("patients") ? [
      { id: "new-patient", label: "New patient", keywords: "add create register", hint: "Opens the form", run: () => navigate("/patients?open=new") },
      { id: "go-patients", label: "Patients", keywords: "roster list search", run: () => navigate("/patients") },
    ] : []),
    ...(canSee("waitlist") ? [
      { id: "waitlist-add", label: "Add to waitlist", keywords: "new waitlist entry cascade", hint: "Opens the form", run: () => navigate("/waitlist?open=new") },
      { id: "go-waitlist", label: "Waitlist", keywords: "cascade fill openings list", run: () => navigate("/waitlist") },
    ] : []),
    ...(canSee("referrals") ? [{ id: "go-referrals", label: "Referrals & Auths", keywords: "referral specialist prior auth authorization appeal", run: () => navigate("/referrals") }] : []),
    ...(canSee("chw_engagements") ? [
      { id: "chw-log", label: "Log engagement (CHW)", keywords: "new engagement community health worker visit contact", hint: "Opens the form", run: () => navigate("/chw-engagements?open=log") },
      { id: "go-chw", label: "CHW engagements", keywords: "community health worker list", run: () => navigate("/chw-engagements") },
    ] : []),
    ...(canSee("my_people") ? [{ id: "go-my-people", label: "My people", keywords: "chw panel caseload assigned", run: () => navigate("/my-people") }] : []),
    // Revenue.
    ...(canSee("billing") ? [{ id: "go-billing", label: "Billing queue", keywords: "charges claims hold", run: () => navigate("/billing") }] : []),
    ...(canSee("eligibility") ? [{ id: "go-eligibility", label: "Eligibility", keywords: "coverage check nctracks 270", run: () => navigate("/eligibility") }] : []),
    ...(canSee("insurance_updates") ? [{ id: "go-insurance", label: "Insurance updates", keywords: "coverage change pending review", run: () => navigate("/insurance-updates") }] : []),
    ...(canSee("recall") ? [{ id: "go-recall", label: "Recall", keywords: "overdue due outreach", run: () => navigate("/recall") }] : []),
    ...(canSee("campaigns") ? [{ id: "go-campaigns", label: "Campaigns", keywords: "outreach blast recall campaign", run: () => navigate("/campaigns") }] : []),
    // Pro AI.
    ...(canSee("pro_chart_prep") ? [{ id: "go-chart-prep", label: "Chart prep", keywords: "pre visit ai prep tomorrow", icon: "\u2726", run: () => navigate("/pro/chart-prep") }] : []),
    ...(canSee("pro_hrsn") ? [{ id: "go-hrsn", label: "HRSN screening", keywords: "social needs sdoh screener", icon: "\u2726", run: () => navigate("/pro/hrsn") }] : []),
    ...(canSee("pro_assistant") ? [{ id: "go-assistant", label: "AI assistant", keywords: "ask analytics question", icon: "\u2726", run: () => navigate("/pro/assistant") }] : []),
    ...(canSee("pro_outreach") ? [{ id: "go-outreach", label: "Outreach", keywords: "sms message campaign draft", icon: "\u2726", run: () => navigate("/pro/outreach") }] : []),
    ...(canSee("cm_copilot") ? [{ id: "go-copilot", label: "CM co-pilot", keywords: "ai drafts review advisories", icon: "\u2726", run: () => navigate("/care-management/copilot") }] : []),
    // Practice administration.
    ...(canSee("staff") ? [
      { id: "staff-add", label: "Add staff member", keywords: "new user invite team hire", hint: "Opens the form", run: () => navigate("/staff?open=new") },
      { id: "go-staff", label: "Staff", keywords: "users team roles list", run: () => navigate("/staff") },
    ] : []),
    ...(role && role !== "Patient" ? [{ id: "go-my-training", label: "My training", keywords: "hipaa training policy acknowledge attest credentials license", run: () => navigate("/my-training") }] : []),
    // Personal settings (own phone, patient-ready texts, desktop alerts). The
    // card writes to the user's own provider record, so it is offered to any
    // staff user who has one - including Providers, who have no /settings.
    ...(role && role !== "Patient" && profile?.provider_id ? [{ id: "go-my-settings", label: "My settings", keywords: "notifications text me patient ready phone desktop alerts sms personal", run: () => navigate("/my-settings") }] : []),
    ...(canSee("consents") ? [{ id: "go-consents", label: "Consents", keywords: "sign document forms", run: () => navigate("/consents") }] : []),
    ...(canSee("compliance") ? [{ id: "go-compliance", label: "Compliance", keywords: "audit hipaa security", run: () => navigate("/compliance") }] : []),
    ...(canSee("insights") ? [{ id: "go-insights", label: "Insights", keywords: "reports export analytics benchmarks snapshot", run: () => navigate("/insights") }] : []),
    // Settings sections that are real routes.
    ...(role === "Owner" || role === "Manager" ? [
      // Every section lives in the one Settings shell at ?tab=<id> (the old
      // /settings/practice/:section only redirects there). Canonical ids: Patient
      // migration and AI usage are parts of the merged ehr_migration / ai pages.
      { id: "set-ehr-migration", label: "EHR migration", keywords: "import onboard practice migrate athena fhir", run: () => navigate("/settings?tab=ehr_migration") },
      { id: "set-patient-migration", label: "Patient migration", keywords: "import demographics csv patients", run: () => navigate("/settings?tab=ehr_migration") },
      { id: "set-locations", label: "Locations", keywords: "sites address service location code", run: () => navigate("/settings?tab=locations") },
      { id: "set-network-access", label: "Network access", keywords: "cin session revoke bh opt in sensitive measures", run: () => navigate("/settings?tab=network_access") },
      { id: "set-kiosks", label: "Kiosks", keywords: "check in tablet pairing", run: () => navigate("/settings?tab=kiosks") },
      { id: "set-payments", label: "Online payments", keywords: "stripe copay pay link", run: () => navigate("/settings?tab=online_payments") },
      { id: "set-hours", label: "Hours and holidays", keywords: "schedule availability closed", run: () => navigate("/settings?tab=hours") },
      { id: "set-appt-types", label: "Appointment types", keywords: "visit types duration", run: () => navigate("/settings?tab=appt_types") },
      { id: "set-ai-usage", label: "AI usage", keywords: "tokens cost spend model", run: () => navigate("/settings?tab=ai") },
      { id: "set-practice", label: "Practice settings", keywords: "configuration setup identity", run: () => navigate("/settings") },
    ] : []),
    // Billing reaches /settings for the Billing tab only (App.jsx SETTINGS_ROLES).
    ...(role === "Owner" || role === "Manager" || role === "Billing" ? [
      { id: "set-billing", label: "Billing settings", keywords: "billers csv templates routing rules", run: () => navigate("/settings?tab=billing") },
    ] : []),
    // Network and super admin.
    // Same gate as the sidebar's Network section (org membership), not the
    // role nav list: org_console is in no role's list, so canSee() hid the
    // palette entry from every network member.
    ...(hasOrg ? [{ id: "go-org", label: "Network console", keywords: "cin organization network attribution rollup this week brief", run: () => navigate("/org") }] : []),
    ...(isSuperAdmin ? [{ id: "go-admin", label: "Administrator", keywords: "super admin practices subscriptions sentinel", run: () => navigate("/admin") }] : []),
    // Always available.
    { id: "shortcuts", label: "Show keyboard shortcuts", keywords: "help keys hotkeys cheatsheet", icon: "\u2328", run: () => setShortcutsOpen(true) },
  ];

  return (
    <AssistantDrawerProvider>
    <div className="pos-app-h" style={{
      display: "flex",
      background: "var(--brand-canvas, " + C.bgTertiary + ")",
      fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
      color: C.textPrimary,
    }}>
      <CommandPalette
        open={cmdkOpen}
        onClose={() => setCmdkOpen(false)}
        pages={cmdkPages}
        actions={cmdkActions}
        onNavigate={(path) => { navigate(path); setCmdkOpen(false); }}
      />
      <ShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <RolePreviewSheet
        open={roleSheetOpen}
        roles={[realRole, ...ROLE_PREVIEWS.filter((r) => r !== realRole)].filter(Boolean)}
        current={viewAsRole || realRole}
        realRole={realRole}
        onPick={setViewAsRole}
        onClose={() => setRoleSheetOpen(false)}
      />
      <DarkPreviewBanner active={!DARK_MODE_READY && previewOn && themePref !== "light"} />
      {/* Automatic logoff: 30 minutes without activity, 60-second warning. */}
      <IdleSignOut minutes={30} warnSeconds={60} onSignOut={signOut} audience="staff" />
      {/* Care team drivers: sends the phone's location while a ride is near
          and sharing is on (ride leave-by alerts). Renders nothing. */}
      <DriverLocationReporter />
      {/* ── Sidebar ───────────────────────────────────────────────────── */}
      {/* Mobile: dim scrim behind the off-canvas drawer; tap to close. */}
      {isMobile && !collapsed && (
        <div
          onClick={() => setCollapsed(true)}
          style={{ position: "fixed", inset: 0, zIndex: 199, background: "rgba(15, 23, 42, 0.45)" }}
        />
      )}

      {/* Mobile: off-canvas overlay drawer so the rail never eats content
          width; desktop keeps the inline 64/220 collapsible rail. */}
      <nav style={isMobile ? {
        position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 200,
        width: 240, maxWidth: "82vw",
        background: C.navBg,
        color: C.onAccent,
        display: "flex",
        flexDirection: "column",
        transform: collapsed ? "translateX(-100%)" : "translateX(0)",
        transition: "transform 0.22s ease",
        boxShadow: collapsed ? "none" : "4px 0 24px rgba(0,0,0,0.28)",
      } : {
        width: collapsed ? 64 : 220,
        background: C.navBg,
        color: C.onAccent,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.2s",
        flexShrink: 0,
      }}>
        <div style={{
          padding: collapsed ? "18px 0" : "18px 20px",
          borderBottom: "0.5px solid rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          justifyContent: collapsed ? "center" : "flex-start",
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: "var(--brand-sidebar-logo-bg, " + C.teal + ")",
            color: "var(--brand-sidebar-logo-fg, #fff)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 800, flexShrink: 0,
          }}>P</div>
          {!collapsed && (
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: C.onAccent, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{practiceName || "PracticeOS"}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{practiceName ? ("PracticeOS \u00b7 " + (tier || "Lite")) : (tier || "\u2014")}</div>
            </div>
          )}
          {isMobile && (
            <button
              onClick={() => setCollapsed(c => !c)}
              style={{
                marginLeft: collapsed ? 0 : "auto",
                background: "transparent", color: C.onAccent,
                border: "0.5px solid rgba(255,255,255,0.25)", borderRadius: 6,
                padding: "6px 9px", fontSize: 13, cursor: "pointer", lineHeight: 1,
              }}
              title={collapsed ? "Open menu" : "Collapse menu"}
            >
              {collapsed ? "\u203A" : "\u2039"}
            </button>
          )}
        </div>

        {/* Nav items - walk NAV_SECTIONS, intersect with role's navItems,
            skip empty sections. The Care Management . AI section (CM Co-pilot)
            is injected dynamically right AFTER the care_mgmt declared section
            so it lives above PRACTICE in the rail. Pro and Super Admin render
            below as their own sections because their item lists come from
            prefs/flags. */}
        {/* Quick search + notifications utility row. */}
        <div style={{
          display: "flex", gap: 6, alignItems: "center",
          margin: collapsed ? "10px auto 2px" : "10px 12px 2px",
          width: collapsed ? "auto" : "calc(100% - 24px)",
          flexDirection: collapsed ? "column" : "row",
        }}>
          <button
            onClick={() => setCmdkOpen(true)}
            className="pos-nav-item"
            title={"Search (" + (isMac ? "Cmd" : "Ctrl") + " K)"}
            style={{
              flex: collapsed ? "none" : 1, width: collapsed ? 38 : "auto", minWidth: 0,
              display: "flex", alignItems: "center", gap: 8,
              justifyContent: collapsed ? "center" : "flex-start",
              background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.72)",
              border: "0.5px solid rgba(255,255,255,0.14)", borderRadius: 8,
              padding: collapsed ? "8px 0" : "8px 10px", fontSize: 12, cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <span style={{ display: "inline-flex", flexShrink: 0 }}><IconSearch size={15} /></span>
            {!collapsed && <span style={{ flex: 1, textAlign: "left" }}>Search</span>}
            {!collapsed && <kbd style={{ fontSize: 9, opacity: 0.75, border: "0.5px solid rgba(255,255,255,0.2)", borderRadius: 4, padding: "0 4px", fontFamily: "inherit" }}>{isMac ? "\u2318K" : "Ctrl K"}</kbd>}
          </button>
          <NotificationsBell
            accessibleNavIds={navIdSet}
            onNavigate={(path, state) => navigate(path, state ? { state } : undefined)}
            collapsed={collapsed}
          />
        </div>

        <div style={{
          flex: 1, padding: "12px 8px", overflowY: "auto",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0, #000 14px, #000 calc(100% - 16px), transparent 100%)",
          maskImage: "linear-gradient(to bottom, transparent 0, #000 14px, #000 calc(100% - 16px), transparent 100%)",
        }}>
          {NAV_SECTIONS.map((section) => {
            const visibleItems = section.items.filter(id => navItems.includes(id));
            if (visibleItems.length === 0) {
              // Even if the declared section is empty for this role, we still
              // need to inject Care Mgmt . AI immediately after care_mgmt so
              // its position in the rail is stable. The injected block has
              // its own showCmCopilot gate below, so an empty role-section
              // collapsing won't accidentally hide the AI section.
              if (section.key === "care_mgmt" && showCmCopilot) {
                return (
                  <CmCopilotAiSection
                    key="care_mgmt_ai"
                    collapsed={collapsed}
                    activeNav={activeNav}
                    onNav={onNav}
                  />
                );
              }
              return null;
            }
            return (
              <div key={section.key}>
                {!collapsed && (
                  <div style={{
                    padding: "14px 12px 6px",
                    fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "color-mix(in srgb, var(--brand-primary-mid, #5DCAA5) 78%, white)",
                  }}>{section.label}</div>
                )}
                {visibleItems.map(id => {
                  const meta = NAV_META[id];
                  if (!meta) return null;
                  const active = activeNav === id;
                  return (
                    <button
                      key={id}
                      data-testid={"nav-" + id.replace(/_/g, "-")}
                      aria-current={active ? "page" : undefined}
                      className="pos-nav-item"
                      title={collapsed ? (meta.label + (badgeCounts[id] > 0 ? " (" + badgeCounts[id] + ")" : "")) : badgeError[id] ? "Couldn't load this count" : undefined}
                      onClick={() => onNav(id)}
                      onMouseEnter={(e) => {
                        if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                      }}
                      onMouseLeave={(e) => {
                        if (!active) e.currentTarget.style.background = "transparent";
                      }}
                      style={navItemStyle(active, collapsed)}
                    >
                      <span style={{ position: "relative", display: "inline-flex" }}>
                        <NavFillGlyph id={id} active={active} count={badgeCounts[id] || 0} />
                        {collapsed && badgeCounts[id] > 0 && (
                          <span style={{
                            position: "absolute", top: -6, right: -9,
                            minWidth: 14, height: 14, padding: "0 3px", borderRadius: 8,
                            background: NAV_FILL_COLOR, color: "#412402", fontSize: 8, fontWeight: 700,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            lineHeight: 1, boxSizing: "border-box", border: "1.5px solid #0a0a0a",
                          }}>{badgeCounts[id] > 9 ? "9+" : badgeCounts[id]}</span>
                        )}
                      </span>
                      {!collapsed && <span style={{ flex: 1 }}>{meta.label}</span>}
                      {!collapsed && <OdometerCount count={badgeCounts[id] || 0} testId={"nav-count-" + id.replace(/_/g, "-")} />}
                    </button>
                  );
                })}
                {/* Inject the Care Management . AI section directly after
                    the care_mgmt section so CM Co-pilot lives above PRACTICE. */}
                {section.key === "care_mgmt" && showCmCopilot && (
                  <CmCopilotAiSection
                    collapsed={collapsed}
                    activeNav={activeNav}
                    onNav={onNav}
                  />
                )}
              </div>
            );
          })}

          {/* Pro · AI section. Items come from prefs (isAiFeatureEnabled,
              isHrsnEnabled), so this section is computed at render time
              rather than declared in NAV_SECTIONS. */}
          {proNavIds.length > 0 && !collapsed && (
            <div style={{
              padding: "14px 12px 6px",
              fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "color-mix(in srgb, var(--brand-primary-mid, #5DCAA5) 78%, white)",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              {/* Lite practices see only Inbound SMS here (texts work on
                  every tier; the AI tools stay Pro). */}
              <span>{isProTier ? "Pro · AI" : "Messages"}</span>
              {isProTier && <span style={{ color: "#5DCAA5", letterSpacing: "0.05em", fontSize: 8 }}>● ON</span>}
            </div>
          )}
          {proNavIds.map(id => {
            const meta = PRO_NAV_META_LOCAL[id];
            if (!meta) return null;
            const active = activeNav === id;
            return (
              <button
                key={id}
                aria-current={active ? "page" : undefined}
                className="pos-nav-item"
                title={collapsed ? meta.label : undefined}
                onClick={() => onNav(id)}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = "transparent";
                }}
                style={navItemStyle(active, collapsed)}
              >
                <NavGlyph id={id} active={active} />
                {!collapsed && <span style={{ flex: 1 }}>{meta.label}</span>}
                {id === "pro_inbound_sms" && inboundSms.crisis > 0 && (
                  <span title="Safety flag" aria-label="Safety flag" style={{ width: 8, height: 8, borderRadius: 999, background: C.dangerSolid, flexShrink: 0 }} />
                )}
                {id === "pro_inbound_sms" && !collapsed && <OdometerCount count={inboundSms.badge} />}
              </button>
            );
          })}
          {/* Network section - only rendered when the user belongs to an
              organization (MSO/CIN). Cosmetic gate; enforcement is RLS +
              the membership-gated org_kpi_rollup RPC. */}
          {hasOrg && (
            <>
              {!collapsed && (
                <div style={{
                  padding: "14px 12px 6px",
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "color-mix(in srgb, var(--brand-primary-mid, #5DCAA5) 78%, white)",
                }}>Network</div>
              )}
              <button
                onClick={() => navigate("/org")}
                className="pos-nav-item"
                title={collapsed ? "Network Console" : undefined}
                onMouseEnter={(e) => {
                  if (location.pathname !== "/org") e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== "/org") e.currentTarget.style.background = "transparent";
                }}
                style={navItemStyle(location.pathname === "/org", collapsed)}
              >
                <NavGlyph id="org_console" active={location.pathname === "/org"} />
                {!collapsed && <span style={{ flex: 1 }}>Network Console</span>}
              </button>
            </>
          )}
          {/* Super admin section - only rendered for super admins.
              Defense layer 1 of 3 (cosmetic). Layer 2 = SuperAdminRoute. Layer 3 = RLS. */}
          {isSuperAdmin && (
            <>
              {!collapsed && (
                <div style={{
                  padding: "14px 12px 6px",
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: "color-mix(in srgb, var(--brand-primary-mid, #5DCAA5) 78%, white)",
                }}>Super Admin</div>
              )}
              <button
                onClick={() => navigate("/admin/subscriptions")}
                className="pos-nav-item"
                title={collapsed ? "Administrator" : undefined}
                onMouseEnter={(e) => {
                  if (!location.pathname.startsWith("/admin")) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                }}
                onMouseLeave={(e) => {
                  if (!location.pathname.startsWith("/admin")) e.currentTarget.style.background = "transparent";
                }}
                style={navItemStyle(location.pathname.startsWith("/admin"), collapsed)}
              >
                <NavGlyph id="administrator" active={location.pathname.startsWith("/admin")} />
                {!collapsed && <span style={{ flex: 1 }}>Administrator</span>}
                {!collapsed && (
                  <span style={{
                    fontSize: 8, fontWeight: 700,
                    color: "#5DCAA5",
                    background: "rgba(93,202,165,0.15)",
                    padding: "1px 5px",
                    borderRadius: 4,
                    letterSpacing: "0.12em",
                  }}>IC</span>
                )}
              </button>
            </>
          )}
        </div>

        {/* ---- Account footer (Round 3, item 6) ----------------------------
            One compact row - avatar, name, role, collapse - instead of the
            305px stack (What's new, user strip, My training, View as, Text
            size, Appearance, Collapse). Every control moved into the account
            menu with its behaviour, storage keys and gates unchanged; the
            gates are evaluated here so they stay next to the state they read.
            On a phone "collapsed" means the drawer is off screen, not a narrow
            rail, so the footer keeps its full row there. */}
        <AccountMenu
          collapsed={collapsed && !isMobile}
          hidden={isMobile && collapsed}
          isMobile={isMobile}
          name={profile?.full_name || ""}
          roleLabel={roleStyle.label || role || "\u2014"}
          roleDot={roleStyle.navDot || C.navMuted}
          previewing={previewingRole}
          showMySettings={Boolean(role && role !== "Patient" && profile?.provider_id)}
          showMyTraining={Boolean(role && role !== "Patient")}
          onNavigate={(path) => { if (isMobile) setCollapsed(true); navigate(path); }}
          viewAs={(realRole === "Owner" || isSuperAdmin) ? {
            current: viewAsRole || realRole,
            previewing: previewingRole,
            realRole,
            onOpen: () => setRoleSheetOpen(true),
            onBack: () => setViewAsRole(null),
          } : null}
          textScale={textScale}
          onTextScale={changeTextScale}
          theme={(DARK_MODE_READY || (DARK_MODE_PREVIEW && (role === "Owner" || isSuperAdmin))) ? {
            ready: DARK_MODE_READY,
            previewOn,
            pref: themePref,
            onPick: (v) => { setThemePreference(v); setThemePref(v); },
            onTryDark: () => { setPreviewOptIn(true); setPreviewOn(true); setThemePreference("dark"); setThemePref("dark"); },
            onLeavePreview: () => { setPreviewOptIn(false); setPreviewOn(false); setThemePref("light"); },
          } : null}
          onSignOut={signOut}
          onToggleCollapse={isMobile ? null : () => setCollapsed((c) => !c)}
        />
      </nav>

      {/* ── Main Content ──────────────────────────────────────────────── */}
      <main ref={mainRef} style={{ flex: 1, minWidth: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {/* Mobile menu bar - the only affordance to open the off-canvas nav,
            since the rail no longer sits inline on phones. Sticky so it stays
            reachable while the page scrolls. */}
        {isMobile && (
          <div data-pos-mobile-bar="1" style={{
            position: "sticky", top: 0, zIndex: 60, flexShrink: 0,
            display: "flex", alignItems: "center", gap: 10,
            padding: "4px 12px 4px 8px",
            background: C.bgPrimary,
            borderBottom: "0.5px solid " + C.borderLight,
          }}>
            {/* 44px hit target (the phone minimum); the bar stays 52px tall. */}
            <button
              onClick={() => setCollapsed(false)}
              title="Open menu"
              aria-label="Open menu"
              style={{
                width: 44, height: 44, flexShrink: 0,
                display: "grid", placeItems: "center",
                // Borderless icon button: next to a page title a boxed button
                // read as a second heading element; the 44px hit area stays.
                background: "transparent", color: C.textPrimary,
                border: "none", borderRadius: 8,
                cursor: "pointer",
              }}
            >
              <span style={{ position: "relative", display: "inline-flex" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
                {/* The rail is off screen on a phone, so the menu button carries
                    the same Inbox number as the sidebar badge (round 4 #3). */}
                {badgeCounts.inbox > 0 && (
                  <span data-testid="phone-inbox-count" aria-label={badgeCounts.inbox + " open inbox items"} style={{
                    position: "absolute", top: -7, right: -10,
                    minWidth: 15, height: 15, padding: "0 4px", borderRadius: 8, boxSizing: "border-box",
                    background: NAV_FILL_COLOR, color: "#412402", fontSize: 9, fontWeight: 700, lineHeight: "15px", textAlign: "center",
                    border: "1.5px solid " + C.bgPrimary,
                  }}>{badgeCounts.inbox > 99 ? "99+" : badgeCounts.inbox}</span>
                )}
              </span>
            </button>
            {/* The current page, not the product name: on a phone this bar is
                the only always-visible chrome, so it says where you are. The
                product name stays in the off-canvas menu header. minWidth 0 +
                ellipsis keep a long title (a full name) on one line. */}
            <span data-pos-phone-title="1" style={{
              flex: 1, minWidth: 0,
              fontFamily: "'Inter', sans-serif", fontSize: 16, fontWeight: 700, color: C.textPrimary, letterSpacing: "-0.01em",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {phoneTitle}
            </span>
          </div>
        )}
        <SpectatorBanner />
        <ProSystemAlertBanner practiceId={practiceId} role={role} tier={tier} />
        {eodNudge && (
          <EodNudge
            count={eodNudge.count}
            isMobile={isMobile}
            onView={() => { setEodNudge(null); navigate("/dashboard"); }}
            onDismiss={() => setEodNudge(null)}
          />
        )}
       {/* Expose onNav to views via outlet context for backward compat.
            Views that still use `{ onNav }` as a prop will silently receive
            undefined and break only if they actually call it. Any such view
            is a one-line fix (see handoff notes below). */}
        {/* Suspense lives HERE, inside the dark shell, so a lazily loaded
            view suspends only the content pane on first visit. The sidebar
            and page background stay painted. (An outer boundary in App.jsx
            suspended the whole tree including this shell, which tore it
            down to a bare white page for the duration of the chunk fetch -
            the bright flash users saw on first navigation.) */}
        {/* One page's failure stays in that page (no white screen); keyed by
            page (routeKey) so navigating to another page clears it. A stale
            bundle after a deploy reloads once (lib/chunkReload). */}
        <RouteErrorBoundary key={pageRouteKey}>
        <Suspense fallback={<PageSkeleton />}>
          {/* Route transition (UI Excellence item 4): the pane re-mounts per
              path and rises in over 220ms. Opacity + transform only, so it is
              GPU-composited and honors prefers-reduced-motion via styles.css. */}
          {/* Route transition. This animates OPACITY ONLY, deliberately.
              The original used a translateY keyframe with fill-mode both; an
              element with a transform becomes the containing block for every
              position:fixed descendant, which clipped every modal, drawer,
              palette and toast in the app to this pane. Opacity has no such
              effect. If a translate is ever wanted back here, it must be on a
              child that contains no overlays.
              Fill-mode BACKWARDS, not both: a fill that holds the end state
              keeps the opacity animation applied forever, and an element with
              an applied opacity animation is a stacking context. Every drawer
              and modal on the page then painted under the phone top bar
              (z-index 60), so the episode drawer's close button could not be
              tapped. Backwards only covers the start; once the fade ends the
              wrapper is a plain box again.
              Keyed by PAGE (routeKey), not pathname: chart and Care Management
              sections are path segments, and a pathname key remounted the
              whole page (and re-ran its reads and read audit) on every
              section click (round 6 #1). The fade plays on a page change only.
              A flex column that grows into <main> (itself a flex column), so
              a view root with flex: 1 fills to the bottom of the screen. As a
              plain block it stopped at its content and the page background
              ended mid-screen (round 6 bug 1). minHeight 0 lets a view that
              scrolls inside itself shrink to the pane instead of making
              <main> scroll as well. */}
          <div key={pageRouteKey} data-pos-route-pane="1" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, animation: "pos-routeFade 200ms cubic-bezier(0.2,0.8,0.2,1) backwards", zoom: "var(--pos-text-scale, 1)" }}>
            <Outlet context={{ onNav }} />
          </div>
        </Suspense>
        </RouteErrorBoundary>
      </main>
      {/* Sits beside <main> in the flex row, so the page COMPRESSES rather
          than being covered. The user keeps sight of what they asked about -
          which is the whole reason this surface exists. */}
      <AssistantDrawer />
    </div>
    </AssistantDrawerProvider>
  );
}

// ---- End-of-day nudge (round 4 #9) ------------------------------------------
// One slim line at the top of the page, in the document flow: it pushes the
// page down by its height instead of floating over it, so it can never cover
// a Save button, a sticky footer or the Inbox key hints. Shown only when the
// "Before you go" sweep has something open for this user, with the count.
function EodNudge({ count, isMobile, onView, onDismiss }) {
  const tall = isMobile ? { minHeight: 44, minWidth: 44 } : null;
  return (
    <div data-testid="eod-nudge" role="status" style={{
      display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
      margin: isMobile ? "8px 12px 0" : "12px 24px 0",
      padding: isMobile ? "4px 4px 4px 12px" : "6px 6px 6px 14px",
      background: C.bgPrimary, color: C.textPrimary,
      border: "0.5px solid " + C.borderMid, borderLeft: "3px solid " + C.warnSolid,
      borderRadius: 10, boxShadow: C.shadowSm, fontSize: 13,
    }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ fontWeight: 700 }}>Before you go:</strong> {eodNudgeText(count)}
      </span>
      <Btn size="sm" variant="outline" onClick={onView} style={tall || {}}>View</Btn>
      <button onClick={onDismiss} aria-label="Dismiss" title="Dismiss" style={{
        background: "transparent", border: "none", color: C.textTertiary, cursor: "pointer",
        fontSize: 16, lineHeight: 1, borderRadius: 6, fontFamily: "inherit",
        width: isMobile ? 44 : 28, height: isMobile ? 44 : 28, flexShrink: 0,
      }}>&times;</button>
    </div>
  );
}

// ─── Care Management · AI section ──────────────────────────────────────────
// Extracted into its own component so the main render can inject it at the
// right position (right after the care_mgmt declared section, before
// practice). Single inline item: CM Co-pilot. Renders nothing when collapsed
// except the icon-only button itself; header label is hidden in collapsed
// rail like every other section.
function CmCopilotAiSection({ collapsed, activeNav, onNav }) {
  return (
    <>
      {!collapsed && (
        <div style={{
          padding: "14px 12px 6px",
          fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "color-mix(in srgb, var(--brand-primary-mid, #5DCAA5) 78%, white)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span>Care Management · AI</span>
          <span style={{ color: "#5DCAA5", letterSpacing: "0.05em", fontSize: 8 }}>ON</span>
        </div>
      )}
      <button
        onClick={() => onNav("cm_copilot")}
        className="pos-nav-item"
        title={collapsed ? "CM Co-pilot" : undefined}
        onMouseEnter={(e) => {
          if (activeNav !== "cm_copilot") e.currentTarget.style.background = "rgba(255,255,255,0.06)";
        }}
        onMouseLeave={(e) => {
          if (activeNav !== "cm_copilot") e.currentTarget.style.background = "transparent";
        }}
        style={navItemStyle(activeNav === "cm_copilot", collapsed)}
      >
        <NavGlyph id="cm_copilot" active={activeNav === "cm_copilot"} />
        {!collapsed && <span style={{ flex: 1 }}>CM Co-pilot</span>}
      </button>
    </>
  );
}
